import './HomePageStyle.less';

import Cookies from 'js-cookie';
import * as React from 'react';
import { Helmet } from 'react-helmet';

import { ImgixImg, ScrollEnter } from '@opendoor/bricks';
import * as analytics from '../api/analytics';
import SneakyElement from '../components/common/SneakyElement';
import SneakyTracker from '../components/common/SneakyTracker';
import ValuePropsSection from '../components/common/ValuePropsSection';
import SellerReturnExperience from '../containers/sellerReturnExperience/SellerReturnExperience';
import { RAILS_ENV } from '../helpers/globals';
import imgixUrl from '../helpers/imgixUrl';
import layoutWrapper from '../hocs/layoutWrapper';

import { EmailCallToAction } from './EmailCallToAction';
import { SellerFlowAddressInput } from './SellerFlowAddressInput';

const DefaultHero = props => {
  return (
    <section className="test-default" id="hero-b">
      <div className="regular-container">
        <div className="hero-container">
          <h1 className="medium-headline hero-headline">
            Get an offer on your home with the press of a button.
          </h1>
          <SneakyTracker trackerId="address-bar">
            <SellerFlowAddressInput
              channel="web"
              onChooseInvalidEntry={props.onChooseInvalidEntry}
              onQueryChange={props.onQueryChange}
              actionText="Get your free offer"
            />
          </SneakyTracker>
          <ScrollEnter>
            <p className="sub-input text-white fade-in">
              Sell your home to Opendoor so you can skip the hassle of listing, showings, and months
              of uncertainty.
            </p>
          </ScrollEnter>
        </div>
      </div>
      <div className="value-props-pins">
        <ScrollEnter>
          <div className="pin pin-not-opendoor flip-in-y hidden-md-down delay-200ms">
            <div className="pin-label"> Listed on the market</div>
            <div className="pin-testimonial text-small">
              “Listed for 120 days, 15 showings, and months of stress.”
            </div>
          </div>
        </ScrollEnter>
        <ScrollEnter>
          <div className="pin pin-opendoor flip-in-y hidden-md-down delay-200ms">
            <div className="pin-label"> Sold to Opendoor</div>
            <div className="pin-testimonial text-small"> “Best sales experience of my life.”</div>
          </div>
        </ScrollEnter>
      </div>
      <div className="press">
        <div className="regular-container text-center">
          <ImgixImg src="48c139818b5b4865bc2f458b0341641f.png" height={30} className="press-logo" />
          <ImgixImg src="7f4224120d8742b6894f1e3d7f27a5f9.png" height={30} className="press-logo" />
          <ImgixImg src="46085b81407c46ee843561ec62ba3df9.png" height={30} className="press-logo" />
          <ImgixImg src="4f695be26d144e438b390178b0ffc651.png" height={30} className="press-logo" />
          <ImgixImg src="fcf57b9d215f4577ab316019da7c7064.png" height={30} className="press-logo" />
        </div>
      </div>
    </section>
  );
};

interface IHowItWorksProps {
  vimeo: string;
  background: string;
  className: string;
}

const HowItWorks = (props: IHowItWorksProps) => {
  const iframe =
    RAILS_ENV === 'test' ? (
      undefined
    ) : (
      <iframe
        id={`video-${props.className}`}
        className="video-tracking"
        src={props.vimeo}
        width={720}
        height={405}
        frameBorder={0}
      />
    );
  return (
    <section
      className={props.className}
      id="how-it-works"
      style={{ backgroundImage: `url(${imgixUrl(props.background)})` }}
    >
      <div className="regular-container">
        <div className="row">
          <div className="col-xs-12 text-center large-margin-bottom small-margin-top">
            <h2>Sell in a few simple steps</h2>
          </div>
        </div>
        <div className="row medium-margin-top">
          <div className="col-xs-12 col-sm-4 text-center value-prop fade-in-up">
            <div className="step-number">1</div>
            <p className="value-prop-description small-margin-top">
              ​​Enter your home details and get an offer in just a few clicks
            </p>
          </div>
          <div className="col-xs-12 col-sm-4 text-center value-prop fade-in-up">
            <div className="step-number step-in-between">2</div>
            <p className="value-prop-description small-margin-top">
              Accept your offer and complete your free home assessment
            </p>
          </div>
          <div className="col-xs-12 col-sm-4 text-center value-prop fade-in-up">
            <div className="step-number">3</div>
            <p className="value-prop-description small-margin-top">
              Close on your timeline and get paid within a matter of days
            </p>
          </div>
        </div>
      </div>
      <div className="small-container">
        <ScrollEnter>
          <div className="seller-flow-video-container fade-in-up">{iframe}</div>
        </ScrollEnter>
      </div>
    </section>
  );
};
const DefaultHowItWorks = () => {
  return (
    <HowItWorks
      background="b7f3fa361db94b2d9944ca20ef7a6c8f.jpg"
      vimeo="https://player.vimeo.com/video/260298142?api=1&player_id=video-test-default"
      className="test-default"
    />
  );
};
const DefaultEmailCta = () => {
  return (
    <section id="email-cta">
      <div className="regular-container">
        <div className="row">
          <div className="col-xs-12 col-sm-2 text-center">
            <ImgixImg src="3d16d7b7556e47e0b3dfb56db0e6e92c.svg" height="80px" />
          </div>
          <div className="col-xs-12 col-sm-4 text-center-xs-down medium-margin-bottom">
            <h6>Get expert tips in your inbox</h6>
            <p className="small-margin-top">
              Discover ways to increase your home's value and stay on top of local trends.
            </p>
          </div>
          <div className="col-xs-12 col-sm-6">
            <EmailCallToAction
              campaign={'home-page-lead-capture-jan-19'}
              successText={`Thanks, you're in!`}
              inputPlaceholder={'you@example.com'}
              submitText={'Give me tips'}
              submitErrorText={`Sorry, that didn't go through, please check your email and submit again!`}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
const DefaultLocations = () => {
  return (
    <section id="locations">
      <div className="regular-container">
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            <div className="row">
              <div className="col-xs-12 col-sm-10">
                <h3 className="text-white">We’re buying and selling across the country</h3>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-6">
                <p>
                  <a href="https://www.opendoor.com/w/sell/phoenix/" className="text-white">
                    Phoenix, AZ
                  </a>
                </p>
                <p>
                  <a
                    href="https://www.opendoor.com/w/sell/dallas-fort-worth"
                    className="text-white"
                  >
                    Dallas-Fort Worth, TX
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/san-antonio" className="text-white">
                    San Antonio, TX
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/las-vegas" className="text-white">
                    Las Vegas, NV
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/atlanta/" className="text-white">
                    Atlanta, GA
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/nashville" className="text-white">
                    Nashville, TN
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/charlotte" className="text-white">
                    Charlotte, NC
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/raleigh-durham" className="text-white">
                    Raleigh-Durham, NC
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/orlando" className="text-white">
                    Orlando, FL
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/tampa" className="text-white">
                    Tampa, FL
                  </a>
                </p>
              </div>
              <div className="col-xs-6">
                <p className="text-white">
                  <a
                    href="https://www.opendoor.com/w/sell/minneapolis-st-paul"
                    className="text-white"
                  >
                    Minneapolis-St.Paul, MN
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/houston" className="text-white">
                    Houston, TX
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/sacramento" className="text-white">
                    Sacramento, CA
                  </a>
                </p>
                <p>
                  <a href="https://www.opendoor.com/w/sell/riverside" className="text-white">
                    Riverside, CA
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/denver" className="text-white">
                    Denver, CO
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/portland" className="text-white">
                    Portland, OR
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/austin" className="text-white">
                    Austin, TX
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/jacksonville" className="text-white">
                    Jacksonville, FL
                  </a>
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/tucson" className="text-white">
                    Tucson, AZ
                  </a>
                  <sup />
                </p>
                <p className="text-white">
                  <a href="https://www.opendoor.com/w/sell/los-angeles" className="text-white">
                    Los Angeles, CA
                  </a>
                  <sup>NEW</sup>
                </p>
              </div>
            </div>
          </div>
          <div className="col-xs-12 col-sm-6">
            <ImgixImg
              src="ccbbe5811b5a470b9eac9b3aae03b07d.png?auto=compress"
              className="homepage-locations-map"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
const DefaultPerks = () => {
  return (
    <section id="perks-comps">
      <div className="small-container">
        <h2 className="text-center">How Opendoor is different</h2>
      </div>
      <div className="regular-container xl-margin-top">
        <div className="row large-margin-bottom">
          <div className="col-xs-12 col-sm-4 competitor competitor-traditional">
            <div className="competitor-header">
              <h5>Traditional home sale</h5>
            </div>
            <ul>
              <li>List on the market for an average 50 days until closing</li>
              <li>Risk of buyer financing falling through</li>
              <li>Hidden fees and upfront costs</li>
              <li>Search for contractors and manage repairs yourself</li>
            </ul>
          </div>
          <div className="col-xs-12 col-sm-4 competitor competitor-opendoor">
            <div className="competitor-header opendoor">
              <img
                src="https://images.opendoor.com/source/s3/imgdrop-production/cc9a6a5bc1f848df9483456de1ff6d16.svg?service=consumer"
                className="block"
              />
            </div>
            <ul>
              <li>Close in just a few days at a competitive price</li>
              <li>No listing, showings, staging or stress</li>
              <li>Transparent fees and pricing</li>
              <li>Skip the work. We’ll handle repairs and deduct the costs</li>
            </ul>
          </div>
          <div className="col-xs-12 col-sm-4 competitor competitor-flipper">
            <div className="competitor-header">
              <h5>House flipper</h5>
            </div>
            <ul>
              <li>Sell instantly, but well below market value</li>
              <li>For distressed properties needing major repairs</li>
              <li>Home resold for a large profit</li>
              <li>Sell your home as-is at a discount</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
const DefaultOfferComposition = () => {
  return (
    <section id="offer-comps">
      <div className="small-container">
        <h2 className="text-center">How we make you a competitive offer</h2>
      </div>
      <div className="regular-container">
        <div className="row">
          <ScrollEnter className="col-xs-12 col-sm-4 text-center">
            <div className="fade-in-up">
              <img
                src="https://images.opendoor.com/source/s3/imgdrop-production/6a549b36760945e4b29672ccdc509540.svg?service=consumer"
                className="offer-comp-img"
              />
              <h6>Unique inputs from you</h6>
              <p>
                Tell us about your home's most unique features and we'll use those details to give
                you a more precise offer.
              </p>
            </div>
          </ScrollEnter>
          <ScrollEnter className="col-xs-12 col-sm-4 text-center">
            <div className="fade-in-up delay-200ms">
              <img
                src="https://images.opendoor.com/source/s3/imgdrop-production/8d7e304c0e1440508ad0e17a937ed1d7.svg?service=consumer"
                className="offer-comp-img"
              />
              <h6>Neighborhood comparisons</h6>
              <p>
                We assess comparable homes that have recently sold near you, and will share them for
                your review.
              </p>
            </div>
          </ScrollEnter>
          <ScrollEnter className="col-xs-12 col-sm-4 text-center">
            <div className="fade-in-up delay-400ms">
              <img
                src="https://images.opendoor.com/source/s3/imgdrop-production/0101a42c808143abb1085438404b2e8f.svg?service=consumer"
                className="offer-comp-img"
              />
              <h6>Deep experience</h6>
              <p>
                We've purchased thousands of homes, giving us a deep understanding of market
                conditions and trends.
              </p>
            </div>
          </ScrollEnter>
        </div>
        <div className="text-center large-margin-top">
          <a href="https://www.opendoor.com/" className="button primary">
            Get your free offer
          </a>
        </div>
      </div>
    </section>
  );
};
const DefaultTransparency = () => {
  return (
    <section id="transparency">
      <div className="regular-container">
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            <img
              src="https://images.opendoor.com/source/s3/imgdrop-production/d500350b5bee44a3bfcff6df713f039a.svg?service=consumer"
              className="transparency-image"
            />
          </div>
          <div className="col-xs-12 col-sm-6">
            <h2>Know your costs upfront.</h2>
            <ScrollEnter className="transparency-item">
              <h6>Service costs</h6>
              <p>
                We take a service charge to help cover the costs of holding and reselling your home.
                These include property taxes, insurance, maintenance, utilities, and marketing.
              </p>
            </ScrollEnter>
            <ScrollEnter className="transparency-item">
              <h6>Repairs costs</h6>
              <p>
                Similar to any buyer, Opendoor will conduct an{' '}
                <a href="https://www.opendoor.com/w/faq/why-home-condition-assessment/">
                  assessment
                </a>{' '}
                of your home to identify if repairs are needed. If so, you have the option to deduct
                the costs and let us handle all the work.
              </p>
            </ScrollEnter>
            <ScrollEnter className="transparency-item">
              <h6>Closing costs</h6>
              <p>
                Just like a traditional sale, each party is responsible for the fees related to
                title insurance, escrow, and recording & notarization.
              </p>
            </ScrollEnter>
          </div>
        </div>
      </div>
    </section>
  );
};
const DefaultTestimonials = () => {
  return (
    <section className="test-default" id="testimonial-homepage">
      <div className="small-container text-center">
        <h2>We're here to make your move easier</h2>
      </div>
      <div className="regular-container">
        <div className="row xl-margin-top">
          <div className="col-xs-12 col-sm-6">
            <ScrollEnter className="one-testimonial-card">
              <ImgixImg src="3fa3d8879cac47d0bfe7c97337b5df8f.jpg" className="fluid-image" />
              <div className="testimonial-details">
                <img
                  src="https://images.opendoor.com/source/s3/imgdrop-production/19e57165d4764674bcf8d94f663db502.svg?service=consumer"
                  className="testimonial-quotemarks"
                />
                <p className="testimonial-text">
                  We have 2 kids, 3 dogs, and a new baby on the way. By selling our home to
                  Opendoor, we didn’t have to keep it show-ready and everything was taken care of.
                </p>
                <p className="testimonial-details-author">Laura and Jim Boldin</p>
                <p className="testimonial-details-author-metas">Sold to Opendoor, Gilbert, AZ</p>
              </div>
            </ScrollEnter>
            <ScrollEnter className="one-testimonial-card">
              <ImgixImg src="9482278c94f8445a8342764cb9f26600.jpg" className="fluid-image" />
              <div className="testimonial-details">
                <img
                  src="https://images.opendoor.com/source/s3/imgdrop-production/19e57165d4764674bcf8d94f663db502.svg?service=consumer"
                  className="testimonial-quotemarks"
                />
                <p className="testimonial-text">
                  I’ll always have a warm spot for Opendoor because it really made my difficult
                  situation easy. And the fact that there was no obligation made me feel so
                  comfortable.
                </p>
                <p className="testimonial-details-author">Bernie Lake</p>
                <p className="testimonial-details-author-metas">Sold to Opendoor, Roswell, GA</p>
              </div>
            </ScrollEnter>
          </div>
          <div className="col-xs-12 col-sm-6">
            <ScrollEnter className="one-testimonial-card">
              <ImgixImg src="ad5c630e2d8c4211b7d57a6e84c0e638.jpg" className="fluid-image" />
              <div className="testimonial-details">
                <img
                  src="https://images.opendoor.com/source/s3/imgdrop-production/19e57165d4764674bcf8d94f663db502.svg?service=consumer"
                  className="testimonial-quotemarks"
                />
                <p className="testimonial-text">
                  Opendoor’s offer came in right near our appraisal, but we never had to list the
                  house or do showings. For the kind of value Opendoor gives you, it’s just a
                  no-brainer.
                </p>
                <p className="testimonial-details-author">Adam Leon</p>
                <p className="testimonial-details-author-metas">Sold to Opendoor, Phoenix, AZ</p>
              </div>
            </ScrollEnter>
            <ScrollEnter className="one-testimonial-card">
              <ImgixImg src="643b3349d8c3409482f1d7862ba2c95e.jpg" className="fluid-image" />
              <div className="testimonial-details">
                <img
                  src="https://images.opendoor.com/source/s3/imgdrop-production/19e57165d4764674bcf8d94f663db502.svg?service=consumer"
                  className="testimonial-quotemarks"
                />
                <p className="testimonial-text">
                  I have a lot of emotions around change, and leaving the house was hard, but
                  selling to Opendoor was so easy.
                </p>
                <p className="testimonial-details-author">Nikki & Pierre Penda</p>
                <p className="testimonial-details-author-metas">Sold to Opendoor, Atlanta, GA</p>
              </div>
            </ScrollEnter>
          </div>
        </div>
        <div className="text-center large-margin-top">
          <a href="https://www.opendoor.com/w/blog/" className="button primary">
            See more stories
          </a>
        </div>
      </div>
    </section>
  );
};
const DefaultBlog = () => {
  return (
    <section className="test-default" id="blog-articles">
      <div className="regular-container">
        <h2 className="text-center">Learn more about selling your home to Opendoor</h2>
        <div className="row large-margin-top">
          <div className="col-xs-12 col-sm-4">
            <a href="https://www.opendoor.com/w/faq/what-types-of-homes-does-opendoor-purchase/">
              <ImgixImg src="9135fa4b4d1046f699d5d82a1412b6ce.jpg" className="fluid-image" />
              <h6>What types of homes does Opendoor purchase?</h6>
            </a>
          </div>
          <div className="col-xs-12 col-sm-4">
            <a href="https://www.opendoor.com/w/blog/how-opendoor-calculates-your-homes-value">
              <ImgixImg src="f5e083ccdedf4c07b69286bfaa9e80e3.jpg" className="fluid-image" />
              <h6>How Opendoor calculates your home’s value</h6>
            </a>
          </div>
          <div className="col-xs-12 col-sm-4">
            <a href="https://www.opendoor.com/w/blog/how-opendoor-costs-compare-with-traditional-home-sale">
              <ImgixImg src="e88299af8d2a4e20b29be02bafc9f9b6.jpg" className="fluid-image" />
              <h6>How do Opendoor’s costs compare to a traditional sale?</h6>
            </a>
          </div>
        </div>
        <div className="text-center large-margin-top">
          <a href="https://www.opendoor.com/w/blog/">Go to the blog</a>
        </div>
      </div>
    </section>
  );
};
const DefaultMission = () => {
  return (
    <section
      className="test-default"
      id="mission-statement"
      style={{ backgroundImage: `url(${imgixUrl('2ec1b9715afd47e2b8c9984d1b59b346.jpg')})` }}
    >
      <div className="small-container text-center">
        <h2 className="text-white">We've dramatically simplified the buying and selling process</h2>
        <div className="separator light" />
        <p className="text-white medium-margin-top">
          Opendoor is revolutionizing home buying and selling with a streamlined process designed to
          put you at the center of your move.
        </p>
      </div>
    </section>
  );
};
const DefaultPrefooter = () => {
  return (
    <section className="test-default" id="prefooter">
      <div className="regular-container">
        <div className="row">
          <div className="col-md-6 col-xs-12">
            <div className="prefooter-box">
              <h5>Customer reviews</h5>
              <ImgixImg
                src="caa08a5879684dd78e0a330c5f38e9f9.png"
                height={85}
                className="prefooter-box-img"
              />
              <p>Hear from our sellers</p>
              <a href="reviews">Read our reviews (50+)</a>
            </div>
          </div>
          <div className="col-md-6 col-xs-12">
            <div className="prefooter-box">
              <h5>Get in touch</h5>
              <ImgixImg
                src="f43325b749844bb8bc1f6122a858b6c9.png"
                height={85}
                className="prefooter-box-img"
              />
              <p>Feel free to contact us anytime</p>
              <a href="mailto:contact@opendoor.com">contact@opendoor.com</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export class HomePage extends React.Component {
  private addressAttemptCount: number = 0;
  public boundForceUpdate = () => {
    this.forceUpdate();
  };
  public componentDidMount() {
    analytics.register({ 'homepage-2019-test': Cookies.get('homepage-2019-test') || 'no-cookie' });
    analytics.trackEvent('homepage', 'viewed', 'new_homepage', {
      'homepage-2019-test': 'homepage-old-3',
    });
    (window as any).optimizelyCallbacks = (window as any).optimizelyCallbacks || [];
    (window as any).optimizelyCallbacks.push(this.boundForceUpdate);
  }
  public componentWillUnmount() {
    (window as any).optimizelyCallbacks = (window as any).optimizelyCallbacks || [];
    const indexOf = (window as any).optimizelyCallbacks.indexOf(this.boundForceUpdate);
    if (indexOf !== -1) {
      (window as any).optimizelyCallbacks.splice(indexOf, 1);
    }
  }
  public render() {
    const description =
      'Opendoor is the new way to sell your home. ' +
      'Skip the hassle of listing, showings and months of stress, and ' +
      'close on your own timeline. Get a free offer today!';
    return (
      <div>
        <Helmet>
          <title>Opendoor | Sell your home the minute you're ready.</title>
          <meta name="description" content={description} />
        </Helmet>
        <div className="od-homepage">
          <SellerReturnExperience />
          <DefaultHero
            onChooseInvalidEntry={this.onChooseInvalidEntry}
            onQueryChange={this.onQueryChange}
          />
          <SneakyElement trackerId="address-bar" scrollMargin={100}>
            {show =>
              show && (
                <div className="pinned-address-bar">
                  <img
                    src="https://images.opendoor.com/source/s3/imgdrop-production/logo-opendoor.svg?service=consumer"
                    alt="Opendoor"
                    className="sticky-logo"
                  />
                  <div className="seller-input-sticky">
                    <SellerFlowAddressInput
                      channel="web"
                      onChooseInvalidEntry={this.onChooseInvalidEntry}
                      onQueryChange={this.onQueryChange}
                      actionText="Get your free offer"
                      paddingLevel="none"
                      showBorder={false}
                    />
                  </div>
                  <div className="sticky-header__nav">
                    <a href="https://www.opendoor.com/">Sell</a>
                    <a href="https://www.opendoor.com/w/homes">Buy</a>
                    <a href="https://www.opendoor.com/w/how-it-works">How it works</a>
                    <a href="https://www.opendoor.com/w/pricing">Pricing</a>
                  </div>
                </div>
              )
            }
          </SneakyElement>
          <ValuePropsSection />
          <DefaultHowItWorks />
          <DefaultEmailCta />
          <DefaultPerks />
          <DefaultOfferComposition />
          <DefaultTransparency />
          <DefaultLocations />
          <DefaultTestimonials />
          <DefaultBlog />
          <DefaultMission />
          <DefaultPrefooter />
        </div>
      </div>
    );
  }
  private onQueryChange = (newQuery: string, oldQuery: string) => {
    if (oldQuery === '' && newQuery.length > 0) {
      analytics.trackEvent('address-input', 'typing', 'new_homepage', {});
    }
  };
  private onChooseInvalidEntry = () => {
    analytics.trackEvent('address-search', 'submit', 'failed-address-attempt', {
      eventValue: ++this.addressAttemptCount,
    });
  };
}

export default layoutWrapper(HomePage, { footerType: 'FULL' });
